import { useCallback, useEffect, useState } from "react";
import { HeadersFunction, MetaFunction, useLoaderData, useParams } from "react-router";
import { filtereQuoteData as filterQuoteData } from "~/components/QuoteViewer/data";
import QuoteViewer from "~/components/QuoteViewer/QuoteViewer";
import dohaChopaiFilterData from "~/data/dohaChopaiFilter.json";
import { DohaChopaiResponseType } from "~/interface/doha-chopai/doha-chopai";
import { SatsangMediaItem } from "~/interface/satsand-media/satsang-media";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { isBrowser } from "~/utils/envirement";
import { computeEtagFromData } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

interface LoaderData {
  imageData: {
    count: number;
    items: SatsangMediaItem[];
  };
  quoteData: DohaChopaiResponseType;
  authorData: MstPersonType[];
  type: string | null;
  category: number | null;
  chopai_id: number | null;
  source: number | null;
}

export const loader = async ({ params, request }: { params: { lang: string }; request: Request }): Promise<any> => {
  const { lang } = params;

  const url = new URL(request.url);
  const type = url.searchParams.get("type") || null;
  const category = url.searchParams.get("category") || null;
  const chopai_id = url.searchParams.get("id") || null;
  const source = url.searchParams.get("source") || null;

  const apiService = new ApiService(lang || LANG.gujarati);

  const masterRes = await apiService.getMasterData();
  const author: MstPersonType[] = masterRes?.rachayta;
  const categoryData: MstPersonType[] = masterRes?.doha_chopai_category || [];
  const chopaiSource: MstPersonType[] = masterRes?.chopai_source || [];

  let categoryId = null;
  if (category && categoryData.length > 0) {
    const foundCategory = categoryData.find((cat) => cat?.text?.toLowerCase().replace(/\s+/g, "-") === category);
    categoryId = foundCategory?.id || null;
  }

  // Convert source text from URL to ID for API call
  let sourceId = null;
  if (source && chopaiSource.length > 0) {
    const foundSource = chopaiSource.find((src) => src?.text?.toLowerCase().replace(/\s+/g, "-") === source);
    sourceId = foundSource?.id || null;
  }

  // Convert language-specific type to English type for server
  let serverType = null;
  if (type) {
    const foundTypeItem = dohaChopaiFilterData.find((item) => item.enName === type || item.guName === type || item.type === type);
    serverType = foundTypeItem?.type || null;
  }

  const payloadForQuote = {
    pageSize: 20,
    pageIndex: 1,
    type: serverType,
    category: categoryId,
    id: Number(chopai_id) || null,
    source: Number(sourceId) || null,
  };

  const quote_res = await apiService.getDohaChopaiData(payloadForQuote);
  const payloadForWallpaper = { pageSize: quote_res.total || 100, pageIndex: 1 };
  const wallpaper_res = await apiService.getReelWallpaper(payloadForWallpaper);

  const DATA = {
    imageData: {
      count: wallpaper_res?.count || 0,
      items: wallpaper_res?.rows || [],
    },
    quoteData: quote_res,
    authorData: author || [],
    type: serverType,
    category: categoryId,
    chopai_id: chopai_id ? Number(chopai_id) : null,
    source: sourceId,
  };

  const etag = computeEtagFromData(DATA);

  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.CHOPAI.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("chopai", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

const GalleryView = () => {
  const { lang } = useParams();
  const { imageData, quoteData, authorData, type, category, chopai_id, source } = useLoaderData<LoaderData>();

  const { total } = quoteData;
  const [pageIndex, setPageIndex] = useState(1);
  const apiService = new ApiService(LANG.gujarati);

  const [imageResData, setImageData] = useState<string[]>(
    imageData?.items && imageData.items.length > 0
      ? imageData.items.map((item) => item?.img?.m.url).filter((url): url is string => typeof url === "string")
      : Array(10).fill(FALLBACK_IMAGE_URL),
  );

  const [quoteResData, setQuoteResData] = useState(filterQuoteData(quoteData?.rows || []));

  // Prevent pull-to-refresh on scroll up
  useEffect(() => {
    if (!isBrowser) return;
    const preventPullToRefresh = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        e.preventDefault();
      }
    };

    document.addEventListener("touchmove", preventPullToRefresh, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventPullToRefresh);
    };
  }, []);

  const fetchMoreData = useCallback(async () => {
    if (quoteResData.length >= total) return;
    const nextPage = pageIndex + 1;
    const payloadForQuote = { pageSize: 20, pageIndex: nextPage, type: type, category: category, id: chopai_id, source: source };
    const quote_res = await apiService.getDohaChopaiData(payloadForQuote);

    const newQuotes = filterQuoteData(quote_res?.rows || []);

    setQuoteResData((prev) => [...prev, ...newQuotes]);
    setPageIndex(nextPage);
  }, [pageIndex, apiService, type, category, chopai_id, source]);

  useEffect(() => {
    setQuoteResData(filterQuoteData(quoteData?.rows || []));
  }, [lang, quoteData]);

  return (
    <div className="h-dvh w-dvw">
      {imageResData.length > 0 ? (
        <QuoteViewer authorData={authorData} images={imageResData} quotes={quoteResData} fetchMoreData={fetchMoreData} />
      ) : (
        <div className="flex justify-center items-center h-full">
          <p className="text-textColorPrimary">No Chopai Found</p>
        </div>
      )}
    </div>
  );
};

export default GalleryView;
